/**
 * Os tipos de serviço que um contrato pode ter.
 *
 * Estavam escritos à mão em sete lugares, e foi assim que o mesmo tipo ganhou
 * nome diferente sem ninguém ter decidido isso: `pregnancy_check` é
 * "Conferência" no cadastro, "Conf." no card e "DG" no chip do dashboard;
 * `embryo_purchase` é "Compra" em cinco telas e "EMB" numa. Tipo novo entra
 * aqui e aparece nas sete.
 *
 * São TRÊS rótulos porque o espaço é mesmo três vezes diferente, não porque
 * alguém esqueceu de unificar:
 *
 *   `label` — o nome do serviço. Cadastro, cabeçalho do workflow, fatura.
 *   `curto` — onde a linha é apertada: card, lista financeira, ficha de grupo.
 *   `sigla` — o chip do dashboard, que precisa caber em três ou quatro letras.
 *
 * `depende` é o tipo sem o qual este não faz sentido: sincronizar receptora
 * só serve para RECEBER embrião, então protocolo sem TE é protocolo para
 * ninguém. Quem depende some do cadastro enquanto a dependência não está
 * contratada, e sai junto quando ela é desmarcada.
 *
 * VT e DT dependem de FIV pelo mesmo motivo: só se congela o que foi
 * produzido. E, ao contrário dos demais, eles NÃO são etapas de trabalho —
 * são linhas de cobrança que entram POR CIMA do modelo do serviço. Um serviço
 * por prenhez pode ter vitrificação: o que não virou prenhez foi para o
 * botijão, ocupa nitrogênio, e hoje sai de graça. Por isso não estão em
 * SCHEDULE_CHAIN e não aparecem no kanban nem no calendário.
 *
 * A Desvitrificação é igual, mas depende da TE: o trabalho é a transferência do
 * que saiu do botijão. Só embrião de outro laboratório gera cobrança.
 *
 * `desc` é a linha explicativa do cadastro. O rótulo do cadastro trazia a
 * descrição entre parênteses ("OPU (Coleta)") logo acima de "Coleta de
 * óvulos" — dizer duas vezes não deixava mais claro.
 */
export const SERVICE_TYPES = [
  { id: 'protocolo',       label: 'Protocolo',   curto: 'Protoc.', sigla: 'PROT', desc: 'Sincronização das receptoras', depende: 'te' },
  { id: 'opu',             label: 'OPU',         curto: 'OPU',    sigla: 'OPU', desc: 'Coleta de óvulos' },
  { id: 'fiv',             label: 'FIV',         curto: 'FIV',    sigla: 'FIV', desc: 'Produção in vitro' },
  { id: 'te',              label: 'TE',          curto: 'TE',     sigla: 'TE',  desc: 'Transferência' },
  { id: 'vitrificacao',    label: 'Vitrificação', curto: 'Vitri',  sigla: 'VT',  desc: 'Cobrança por embrião vitrificado', depende: 'fiv' },
  { id: 'dt',              label: 'DT',          curto: 'DT',     sigla: 'DT',  desc: 'Cobrança por embrião congelado em DT', depende: 'fiv' },
  { id: 'desvitrificacao', label: 'Desvitrificação', curto: 'Desvitri', sigla: 'DV', desc: 'Cobrança por embrião de outro laboratório descongelado', depende: 'te' },
  { id: 'embryo_purchase', label: 'Compra',     curto: 'Compra', sigla: 'EMB', desc: 'Compra de embriões' },
  { id: 'pregnancy_check', label: 'Conferência', curto: 'Conf.',  sigla: 'DG',  desc: 'Diagnóstico prenhez' },
  { id: 'daily_service',   label: 'Diária',      curto: 'Diária', sigla: 'DIA', desc: 'Serviço por dia' },
];

export const SERVICE_TYPE_BY_ID = Object.fromEntries(SERVICE_TYPES.map(t => [t.id, t]));

/**
 * Tipo desconhecido volta como o próprio id, em vez de sumir.
 *
 * `services_offered` é `text[]` sem restrição no banco: um serviço antigo pode
 * carregar um valor que não existe mais aqui. Mostrar o id cru é feio, mas
 * mostrar vazio esconderia que aquele serviço foi contratado.
 */
const rotulo = (campo) => (id) => SERVICE_TYPE_BY_ID[id]?.[campo] || id;

export const rotuloDoServico = rotulo('label');
export const curtoDoServico  = rotulo('curto');
export const siglaDoServico  = rotulo('sigla');

/** Este tipo pode ser contratado, dado o que já está contratado? */
export const tipoDisponivel = (id, oferecidos = []) => {
  const dep = SERVICE_TYPE_BY_ID[id]?.depende;
  return !dep || oferecidos.includes(dep);
};

/**
 * Tira os tipos que perderam a dependência.
 *
 * Desmarcar a TE tem de levar o protocolo junto: deixá-lo marcado gravaria uma
 * etapa que o serviço nunca vai executar, e ela apareceria como pendente no
 * kanban e no status para sempre.
 */
export const semOsOrfaos = (oferecidos = []) =>
  oferecidos.filter(id => tipoDisponivel(id, oferecidos));
